"use client";

/**
 * Error Boundary for Frontend Routes
 *
 * Catches runtime errors in the frontend application and displays
 * a user-friendly error page with recovery options.
 *
 * This component is required because:
 * - Zod validation failures throw ZodError at runtime
 * - Payload CMS queries can fail (network, database issues)
 * - Transform layer can throw errors
 * - Without this, errors show as 404s (incorrect UX)
 *
 * Following CLAUDE.md:
 * - Brand-agnostic styling with Tailwind
 * - No hardcoded colors (use semantic tokens)
 */

import Link from "next/link";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

interface ErrorProps {
	error: Error & { digest?: string };
	reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorProps) {
	useEffect(() => {
		// Log error to console in development
		// In production, this could send to error tracking service
		console.error("Application error:", error);
	}, [error]);

	// Determine if this is a known error type
	const isZodError = error.name === "ZodError";
	const isPayloadError =
		error.message.includes("Payload") || error.message.includes("FATAL");

	return (
		<main className="container mx-auto px-4 py-16">
			<div className="max-w-2xl mx-auto text-center space-y-8">
				<div className="space-y-4">
					<h1 className="text-6xl font-bold text-destructive">Error</h1>
					<h2 className="text-3xl font-semibold">Something went wrong</h2>
					<p className="text-muted-foreground text-lg">
						{isZodError
							? "There was a problem loading the content for this page."
							: isPayloadError
								? "The content management system encountered an error."
								: "An unexpected error occurred while loading this page."}
					</p>

					{/* Show error details in development */}
					{process.env.NODE_ENV === "development" && (
						<details className="mt-4 text-left bg-muted p-4 rounded-md">
							<summary className="cursor-pointer font-semibold text-sm">
								Error Details (Development Only)
							</summary>
							<pre className="mt-2 text-xs overflow-auto">
								{error.message}
								{error.digest && `\n\nDigest: ${error.digest}`}
							</pre>
						</details>
					)}
				</div>

				<div className="flex flex-col sm:flex-row gap-4 justify-center">
					<Button onClick={reset} size="lg">
						Try again
					</Button>
					<Button asChild variant="secondary" size="lg">
						<Link href="/">Return to homepage</Link>
					</Button>
					<Button asChild variant="outline" size="lg">
						<Link href="/contact">Contact support</Link>
					</Button>
				</div>

				{/* Additional context */}
				<div className="text-sm text-muted-foreground">
					<p>
						If this problem persists, please contact us and reference error
						digest:
					</p>
					<code className="text-xs bg-muted px-2 py-1 rounded">
						{error.digest || "No digest available"}
					</code>
				</div>
			</div>
		</main>
	);
}
